import React, { useState, useMemo, useEffect, useRef } from 'react';
import styles from './PreviewBusiness.module.css';
import TabButton from '../../UI/TabButton/TabButton';
import ScalePreview from '../../UI/ScalePreview/ScalePreview';
import Icon from '../../Icon';
import { BusinessCardData } from '../../CardsComponents/types';
import cn from 'classnames';

const YANDEX_MAP_API_URL = "https://api-maps.yandex.ru/2.1/?lang=ru_RU";

const PreviewBusiness: React.FC<{ activeTab?: string, businessData: BusinessCardData }> = ({ activeTab = "Планировка", businessData }) => {
  const tabs = ["Планировка", "На этаже", "На карте"];
  const [currentTab, setCurrentTab] = useState<string>(activeTab);
  const [zoomLevel, setZoomLevel] = useState(160);
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstance = useRef<any>(null);
  const centerCoordinates = useMemo(() => [42.887141, 47.624338], []);

  useEffect(() => {
    const scriptExists = document.querySelector(`script[src="${YANDEX_MAP_API_URL}"]`);
    if (!scriptExists) {
      const script = document.createElement("script");
      script.src = YANDEX_MAP_API_URL;
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  useEffect(() => {
    if (currentTab === "На карте" && window.ymaps && mapRef.current) {
      window.ymaps.ready(() => {
        if (!mapInstance.current) {
          const initialZoom = 2 + (zoomLevel - 2) * (20 - 2) / (200 - 10);
          mapInstance.current = new window.ymaps.Map(mapRef.current, {
            center: centerCoordinates,
            zoom: initialZoom,
            controls: []
          });

          const handleMouseWheel = (e: WheelEvent) => {
            e.preventDefault();
            const delta = e.deltaY > 0 ? -10 : 10;
            setZoomLevel(prev => Math.max(10, Math.min(prev + delta, 200)));
          };
          if (mapRef.current) {
            mapRef.current.addEventListener('wheel', handleMouseWheel, { passive: false });
          }

          return () => {
            if (mapRef.current) {
              mapRef.current.removeEventListener('wheel', handleMouseWheel);
            }
          };
        }
      });
    } else {
      if (mapInstance.current) {
        mapInstance.current.destroy();
        mapInstance.current = null;
      }
    }
  }, [currentTab, centerCoordinates, zoomLevel]);

  useEffect(() => {
    if (mapInstance.current) {
      const yandexZoomLevel = 2 + ((zoomLevel - 2) * (20 - 2)) / (200 - 10);
      mapInstance.current.setZoom(yandexZoomLevel, {
        duration: 300,
        timingFunction: 'ease-in-out'
      });
    }
  }, [zoomLevel]);

  const handleTabClick = (tab: string) => {
    setCurrentTab(tab);
  };

  const handleLocationClick = () => {
    if (mapInstance.current) {
      mapInstance.current.panTo(centerCoordinates, {
        duration: 500,
        timingFunction: 'ease-out'
      });
    }
  };

  const images: Record<string, string> = {
    "Планировка": businessData.previewPlan || '',
    "На этаже": businessData.previewFloor || ''
  };

  return (
    <div className={styles.plan}>
      <div className={styles.padding_wrapper}>
        <div className={cn(styles.wrapper, (currentTab === "На карте" || currentTab === "На этаже") && styles.no_padding)}>
          <div className={styles.plan_image}>
            {currentTab === "На карте" ? (
              <div ref={mapRef} className={styles.map_container}>
                <div className={styles.controls_bar_map}>
                  <div className={styles.location_wrapper} onClick={handleLocationClick}>
                    <Icon name='locationIcon' width='8' height='8' />
                  </div>
                  <ScalePreview zoom={zoomLevel} setZoom={setZoomLevel} />
                </div>
              </div>
            ) : (
              <img src={images[currentTab]} alt={currentTab} />
            )}
          </div>
        </div>
      </div>
      <div className={styles.tabs_container}>
        {tabs.map((tab, index) => (
          <React.Fragment key={index}>
            <TabButton isActive={currentTab === tab} onClick={() => handleTabClick(tab)} theme='light'>
              {tab}
            </TabButton>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default PreviewBusiness;